/**
 * v2.0.1 BootNotification wire mapping.
 *
 * The version-neutral `BootNotificationDomain` carries v1.6-style fields
 * (chargePointVendor, chargePointModel, …). v2.0.1 nests them under a
 * `chargingStation: { vendorName, model, … }` object and adds a required
 * `reason` enum (BootReasonEnumType). Session 10a always sends
 * `reason: 'PowerUp'` — later sessions can plumb a real reason through
 * `BootNotificationDomain`.
 */
import type {
  BootNotificationDomain,
  BootNotificationResponse,
} from '@/protocol/OcppAdapter';

export type BootReasonV201 =
  | 'ApplicationReset'
  | 'FirmwareUpdate'
  | 'LocalReset'
  | 'PowerUp'
  | 'RemoteReset'
  | 'ScheduledReset'
  | 'Triggered'
  | 'Unknown'
  | 'Watchdog';

export const DEFAULT_BOOT_REASON_V201: BootReasonV201 = 'PowerUp';

export function buildBootNotificationRequest(
  req: BootNotificationDomain,
  reason: BootReasonV201 = DEFAULT_BOOT_REASON_V201,
): Record<string, unknown> {
  const chargingStation: Record<string, unknown> = {
    vendorName: req.chargePointVendor,
    model: req.chargePointModel,
  };
  if (req.chargePointSerialNumber !== undefined) {
    chargingStation['serialNumber'] = req.chargePointSerialNumber;
  } else if (req.chargeBoxSerialNumber !== undefined) {
    chargingStation['serialNumber'] = req.chargeBoxSerialNumber;
  }
  if (req.firmwareVersion !== undefined) {
    chargingStation['firmwareVersion'] = req.firmwareVersion;
  }
  if (req.iccid !== undefined || req.imsi !== undefined) {
    const modem: Record<string, unknown> = {};
    if (req.iccid !== undefined) modem['iccid'] = req.iccid;
    if (req.imsi !== undefined) modem['imsi'] = req.imsi;
    chargingStation['modem'] = modem;
  }
  return { reason, chargingStation };
}

export function parseBootNotificationResponse(
  payload: Record<string, unknown>,
): BootNotificationResponse {
  return {
    status: payload['status'] as BootNotificationResponse['status'],
    currentTime: payload['currentTime'] as string,
    interval: payload['interval'] as number,
  };
}
